/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import initialStateSidebar from './initialState';

import * as S from './styles';

interface IProps {
  userInfo?: any;
  /** Icon-only rail. Owned by the layout, which also drives it off the viewport. */
  collapsed?: boolean;
  /** Called when a collapsed rail has to open to show something — a group's children. */
  onExpand?: () => void;
}

interface INavItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  children?: (INavItem | null)[];
}

/**
 * The label the menu builder hands back is either a plain string (a group) or
 * a <Link> around the text (a leaf). The row draws its own link — an anchor
 * inside an anchor is not valid HTML — so take the text out of it.
 */
const textOf = (label: any): string =>
  typeof label === 'string' ? label : typeof label?.props?.children === 'string' ? label.props.children : '';

/** A leaf is current for its own route and for anything nested under it. */
const isCurrent = (pathname: string, key: string) => pathname === key || pathname.startsWith(`${key}/`);

const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SideBarMenu: React.FC<IProps> = ({ userInfo, collapsed, onExpand }) => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  /**
   * Role gating, unchanged.
   *
   * The builder asks for an item and gets `null` back when the signed-in role
   * is not on its list, so a denied item is absent rather than disabled. That
   * is the whole of the permission model in the nav, and it is the reason this
   * component consumes `initialState` rather than owning a menu of its own.
   */
  function getItem(
    role: string[],
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: (INavItem | null)[],
    type?: 'group'
  ): INavItem {
    if (role?.find((item: string) => item === userInfo?.role)) {
      return { key, icon, children, label, type } as unknown as INavItem;
    } else return null as any;
  }

  const items: INavItem[] = useMemo(
    () => (initialStateSidebar(getItem) as (INavItem | null)[]).filter(Boolean) as INavItem[],
    [userInfo?.role]
  );

  /** Whichever group holds the current route opens itself. */
  useEffect(() => {
    const current = items
      .filter((item) => item.children && isCurrent(location.pathname, item.key))
      .map((item) => item.key);

    if (current.length) setOpenKeys((keys) => Array.from(new Set([...keys, ...current])));
  }, [location.pathname, items]);

  const toggle = (key: string) => {
    // A group cannot show its children at 64px wide, so opening one opens the rail.
    if (collapsed) onExpand?.();
    setOpenKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  };

  const renderLeaf = (item: INavItem) => {
    const label = textOf(item.label);

    return (
      <S.NavLink
        key={item.key}
        to={item.key}
        title={label}
        className={isCurrent(location.pathname, item.key) ? 'active' : ''}
      >
        {item.icon ? <S.Icon>{item.icon}</S.Icon> : <></>}
        <S.Label>{label}</S.Label>
      </S.NavLink>
    );
  };

  return (
    <S.SideContainer className={collapsed ? 'collapsed' : ''}>
      <S.Brand>
        <S.BrandMark src="/logos/logo-mini-white.png" alt="" />
        <S.BrandName>Frontline</S.BrandName>
      </S.Brand>

      <S.Nav>
        {items.map((item) => {
          if (!item.children) return renderLeaf(item);

          const children = item.children.filter(Boolean) as INavItem[];
          const open = openKeys.includes(item.key) && !collapsed;
          const label = textOf(item.label);

          return (
            <S.Group key={item.key}>
              <S.GroupButton
                type="button"
                title={label}
                className={`${open ? 'open' : ''} ${isCurrent(location.pathname, item.key) ? 'active' : ''}`}
                onClick={() => toggle(item.key)}
                aria-expanded={open}
              >
                {item.icon ? <S.Icon>{item.icon}</S.Icon> : <></>}
                <S.Label>{label}</S.Label>
                <S.Chevron>
                  <Chevron />
                </S.Chevron>
              </S.GroupButton>

              <S.Sub className={open ? 'open' : ''}>{children.map(renderLeaf)}</S.Sub>
            </S.Group>
          );
        })}
      </S.Nav>
    </S.SideContainer>
  );
};

export default SideBarMenu;
